import {
  BaseQuizRes,
  FlashcardMediaData,
  FlashcardNodeData,
  MediaData,
  MultipleChoiceMediaData,
  MultipleChoiceNodeData,
  NodeData,
  QuizData,
  QuizItem,
  NumberMediaData,
  TrueFalseNodeData,
  DragAndDropNodeData,
  HotSpotNodeData,
  HotSpotMediaData,
  EnhancedHotSpotNodeData,
  EnhancedHotSpotMediaData,
  GraphicMediaData,
  GraphicNodeData,
  OrderedResponseNodeData,
  AnswerOrder,
  ClozeMediaData,
  ClozeResponseNodeData,
  MatrixNodeData,
  FillInTheBlankNodeData,
  SentenceDragAndDropNodeData,
  QuizHistoryData,
  QuizHistory,
  QuizAnswer,
} from '../../../types/quiz';
import { mapFlashcardData } from './mapFlashcardData';
import { mapMultipleChoiceData } from './mapMultipleChoiceData';
import { mapTrueFalseData } from './mapTrueFalseData';
import { mapGraphicData } from './mapGraphicData';
import { mapDragAndDrop } from './mapDragAndDrop';
import { mapHotSpotData } from './mapHotSpotData';
import { mapOrderedData } from './mapOrderedData';
import { mapMatrixData } from './mapMatrixData';
import { mapFillInTheBlank } from './mapFillInTheBlank';
import { mapEnhancedHotSpotData } from './mapEnhancedHotSpotData';
import { mapClozeData } from './mapClozeData';
import { mapSentenceDragAndDrop } from './mapSentenceDragAndDrop';
import shuffle from 'lodash/shuffle';
import { QuizType } from '@osmosis/shared/types/quiz';

/**
 * MapData.
 */
export const mapData = ({
  subtype,
  mediaData,
  data,
}: {
  subtype: QuizType;
  data: NodeData;
  mediaData: MediaData;
}): QuizData => {
  let res: QuizData;

  switch (subtype) {
    case QuizType.MultipleChoice:
    case QuizType.Exhibit:
    case QuizType.ExtendedMultipleCheckbox:
    case QuizType.MultipleCheckbox:
      res = mapMultipleChoiceData({
        mediaData: mediaData as MediaData<MultipleChoiceMediaData[]>,
        data: data as MultipleChoiceNodeData,
      });
      break;
    case QuizType.Flashcard:
      res = mapFlashcardData({
        mediaData: mediaData as MediaData<FlashcardMediaData[]>,
        data: data as FlashcardNodeData,
      });
      break;
    case QuizType.TrueFalse:
      res = mapTrueFalseData({
        mediaData: mediaData as MediaData<NumberMediaData[]>,
        data: data as TrueFalseNodeData,
      });
      break;
    case QuizType.HotSpot:
      res = mapHotSpotData({
        mediaData: mediaData as MediaData<HotSpotMediaData[]>,
        data: data as HotSpotNodeData,
      });
      break;
    case QuizType.EnhancedHotSpot:
      res = mapEnhancedHotSpotData({
        mediaData: mediaData as MediaData<EnhancedHotSpotMediaData[]>,
        data: data as EnhancedHotSpotNodeData,
      });
      break;
    case QuizType.ExtendedDragAndDrop:
    case QuizType.OrderedDragAndDrop: {
      res = mapDragAndDrop({
        mediaData: mediaData as MediaData<NumberMediaData[]>,
        data: data as DragAndDropNodeData,
      });
      break;
    }
    case QuizType.Graphic: {
      res = mapGraphicData({
        mediaData: mediaData as MediaData<GraphicMediaData[]>,
        data: data as GraphicNodeData,
      });
      break;
    }
    case QuizType.OrderedResponse: {
      res = mapOrderedData({
        mediaData: mediaData as MediaData<MultipleChoiceMediaData[]>,
        data: data as OrderedResponseNodeData,
      });
      break;
    }
    case QuizType.Matrix: {
      return mapMatrixData({
        mediaData: mediaData as MediaData<MultipleChoiceMediaData[]>,
        data: data as MatrixNodeData,
      });
    }
    case QuizType.FillInTheBlankRN:
    case QuizType.FillInTheBlank: {
      return mapFillInTheBlank({
        mediaData: mediaData as MediaData<NumberMediaData[]>,
        data: data as FillInTheBlankNodeData,
      });
    }
    case QuizType.Cloze: {
      res = mapClozeData({
        mediaData: mediaData as MediaData<ClozeMediaData[]>,
        data: data as ClozeResponseNodeData,
      });
      break;
    }
    case QuizType.SentenceDragAndDrop: {
      return mapSentenceDragAndDrop({
        mediaData: mediaData as MediaData<NumberMediaData[]>,
        data: data as SentenceDragAndDropNodeData,
      });
    }
    default:
      return { question: `Undefined map fn for ${subtype}`, answers: [] };
  }

  if ('answerOrder' in res) {
    const newAnswers = handleAnswerRandomization(
      res.answers as QuizAnswer[],
      res.answerOrder,
    );

    res.answers = newAnswers;
  }

  return res;
};

const getHistoryData = (
  nodeID: number,
  historyData: QuizHistoryData,
): QuizHistory | undefined => {
  const { nodeID: nodeIDs, ...restData } = historyData;
  const index = nodeIDs.findIndex((n) => n === nodeID);

  if (index > -1) {
    return Object.entries(restData).reduce(
      (obj, [key, values]) => ({
        ...obj,
        [key]: values[index],
      }),
      {} as QuizHistory,
    );
  }

  return undefined;
};

/**
 * Maps base quiz item data to a properly typed and named object.
 */
export function mapResDataToQuizItem({
  nodeData,
  mediaData: _mediaData,
  historyData,
}: BaseQuizRes): QuizItem[] {
  return shuffle(
    nodeData.nodeID.reduce<QuizItem[]>((acc, nodeID, index) => {
      const data: NodeData = nodeData.data[index];
      const mediaData = {
        ..._mediaData,
        nodeID: _mediaData.nodeID.map((id) => +id),
      };
      const item: Omit<QuizItem, 'data'> = {
        subtype: nodeData.subtype[index],
        userID: nodeData.userID[index],
        nodeID: +nodeID,
      };

      return [
        ...acc,
        {
          ...item,
          data: mapData({ subtype: item.subtype, mediaData, data }),
          history: getHistoryData(item.nodeID, historyData),
        },
      ];
    }, []),
  );
}

function handleAnswerRandomization<T>(
  answers: T[],
  answerOrder = AnswerOrder.Randomized,
) {
  let newAnswers: T[] = [];

  switch (answerOrder) {
    case AnswerOrder.RandomizedExceptLast:
      newAnswers = [
        ...shuffle(answers.slice(0, answers.length - 1)),
        answers[answers.length - 1],
      ];

      break;
    case AnswerOrder.RandomizedExceptLast2:
      newAnswers = [
        ...shuffle(answers.slice(0, answers.length - 2)),
        ...answers.slice(answers.length - 2, answers.length),
      ];

      break;
    case AnswerOrder.RandomizedExceptLast3:
      newAnswers = [
        ...shuffle(answers.slice(0, answers.length - 3)),
        ...answers.slice(answers.length - 3, answers.length),
      ];

      break;
    case AnswerOrder.AsListed:
      newAnswers = answers;
      break;
    default:
      newAnswers = shuffle([...answers]);
      break;
  }

  return newAnswers;
}
